import { useTranslation } from 'react-i18next';
import { Filter, AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { cn } from '@/core/lib/utils';
import type { AllTasksParams } from '../../types';

interface AllTasksFiltersProps {
  filters: AllTasksParams;
  onChange: (filters: AllTasksParams) => void;
  totalCount?: number;
}

const UNSET = '__unset__';

export function AllTasksFilters({ filters, onChange, totalCount }: AllTasksFiltersProps) {
  const { t } = useTranslation('analytics');

  const hasActiveFilters =
    !!filters.projectId ||
    !!filters.statusType ||
    filters.priority !== undefined ||
    !!filters.assigneeId;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        className="h-8 w-64 text-sm"
        placeholder={t('allTasks.search.placeholder')}
        value={filters.search ?? ''}
        onChange={(e) => onChange({ ...filters, search: e.target.value || undefined })}
        data-testid="analytics-all-tasks-search"
      />

      <Button
        type="button"
        size="sm"
        variant={filters.isOverdue ? 'destructive' : 'outline'}
        className="h-8 gap-1.5 text-xs"
        onClick={() => onChange({ ...filters, isOverdue: filters.isOverdue ? undefined : true })}
        data-testid="analytics-all-tasks-overdue-toggle"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        {t('allTasks.filters.overdueOnly')}
      </Button>

      <div className="flex items-center gap-1.5">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                'h-8 gap-1.5 text-xs',
                hasActiveFilters && 'border-primary text-primary',
              )}
              data-testid="analytics-all-tasks-filters-btn"
            >
              <Filter className="h-3.5 w-3.5" />
              {t('allTasks.filters.button')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 space-y-3 p-4" align="start">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                {t('allTasks.filters.status')}
              </p>
              <Select
                value={filters.statusType ?? UNSET}
                onValueChange={(v) =>
                  onChange({
                    ...filters,
                    statusType: v === UNSET ? undefined : (v as AllTasksParams['statusType']),
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNSET}>—</SelectItem>
                  <SelectItem value="Todo">{t('statusType.Todo')}</SelectItem>
                  <SelectItem value="InProgress">{t('statusType.InProgress')}</SelectItem>
                  <SelectItem value="Done">{t('statusType.Done')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">
                {t('allTasks.filters.priority')}
              </p>
              <Select
                value={filters.priority !== undefined ? String(filters.priority) : UNSET}
                onValueChange={(v) =>
                  onChange({
                    ...filters,
                    priority: v === UNSET ? undefined : Number(v),
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNSET}>—</SelectItem>
                  <SelectItem value="0">{t('priority.none')}</SelectItem>
                  <SelectItem value="1">{t('priority.low')}</SelectItem>
                  <SelectItem value="2">{t('priority.medium')}</SelectItem>
                  <SelectItem value="3">{t('priority.high')}</SelectItem>
                  <SelectItem value="4">{t('priority.urgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 w-full text-xs"
                onClick={() =>
                  onChange({
                    search: filters.search,
                    isOverdue: filters.isOverdue,
                    sortBy: filters.sortBy,
                    sortAscending: filters.sortAscending,
                  })
                }
              >
                {t('allTasks.filters.clearFilters')}
              </Button>
            )}
          </PopoverContent>
        </Popover>
        {totalCount != null && totalCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
            {totalCount}
          </span>
        )}
      </div>
    </div>
  );
}
